import React, { memo, useCallback } from 'react';
import { PhysicalProperty, PHYSICAL_PROPERTY_REL_ELEC_DENSITY, PHYSICAL_PROPERTY_REL_MASS_DENSITY } from '../../../store/contouring/contouring-types';
import { Dropdown, DropdownItem, MenuToggle, MenuToggleElement } from '@patternfly/react-core';
import { get, has } from 'lodash-es';

interface PhysicalPropertiesDropdownProps {
    onSelect: (physicalProperty: PhysicalProperty) => void,
    value: string,
    id?: string;
}

const supportedPhysicalProperties: { [x: string]: string } = {
    [PhysicalProperty.NotSet]: ' ',
    [PhysicalProperty.RelElecDensity]: PHYSICAL_PROPERTY_REL_ELEC_DENSITY,
    [PhysicalProperty.RelMassDensity]: PHYSICAL_PROPERTY_REL_MASS_DENSITY,
};

const dropdownItems = [
    <DropdownItem key={PhysicalProperty.NotSet}>&nbsp;</DropdownItem>,
    <DropdownItem key={PhysicalProperty.RelElecDensity}>{PHYSICAL_PROPERTY_REL_ELEC_DENSITY}</DropdownItem>,
    <DropdownItem key={PhysicalProperty.RelMassDensity}>{PHYSICAL_PROPERTY_REL_MASS_DENSITY}</DropdownItem>,
];

const getDropdownEnum = (str: string): PhysicalProperty => {
    switch (str) {
        case '':
            return PhysicalProperty.NotSet;
        case PHYSICAL_PROPERTY_REL_ELEC_DENSITY:
            return PhysicalProperty.RelElecDensity;
        case PHYSICAL_PROPERTY_REL_MASS_DENSITY:
            return PhysicalProperty.RelMassDensity;
        default:
            return PhysicalProperty.NotSet;
    }
}

const PhysicalPropertiesDropdown = (props: PhysicalPropertiesDropdownProps) => {
    const { value, onSelect, id } = props;

    const [isOpen, setIsOpen] = React.useState(false);

    const handleToggle = useCallback(() => {
        setIsOpen(!isOpen);
    }, [isOpen]);

    const handleSelect = useCallback((value: unknown) => {
        setIsOpen(false);
        const innerText = get(value, 'target.innerText', undefined);
        if (innerText !== undefined) {
            const physicalProp = getDropdownEnum(innerText);
            onSelect(physicalProp);
        }
    }, [onSelect]);

    return (
        <Dropdown
            id={id}
            className="physical-properties-dropdown"
            toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                <MenuToggle ref={toggleRef} onClick={handleToggle} isExpanded={isOpen} isFullWidth data-testid="physical-properties-toggle">
                    {supportedPhysicalProperties[value]}
                </MenuToggle>)}
            isOpen={isOpen}
            onSelect={handleSelect}
            onOpenChange={() => setIsOpen(!isOpen)}
        >
            {dropdownItems}
        </Dropdown>
    );
}

export default memo(PhysicalPropertiesDropdown);
